import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LoginIcon from "@mui/icons-material/Login";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import SkipNextOutlinedIcon from "@mui/icons-material/SkipNextOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import {
  completeReviewWorkApi,
  exceptFeedbackApi,
  getReviewContextApi,
  saveReviewChecklistApi,
} from "src/DAL/coachWorkspace/coachWorkspace";
import { member_login_api } from "src/DAL/member/Member1";
import { clientPortalUrl } from "src/config/config";
import { normalizeSubmission } from "src/pages/Trackers/submissionStorage";
import ClientFeedbackDrawer from "src/pages/Trackers/ClientFeedbackDrawer";
import { ActionMenu, Breadcrumbs, CW, ErrorBox, Eyebrow, IconAction, InfoHint, Initials, Loading, PageTitle, Panel, Pill, TextLink, UnderlineTabs } from "./cwUi";
import ProgrammeWeekPanel from "./ProgrammeWeekPanel";
import LatestCheckInPanel from "./LatestCheckInPanel";
import SubmitForClient from "./SubmitForClient";
import CoachNotebook from "./CoachNotebook";
import ReviewChecklist from "./ReviewChecklist";
import ScheduleChangeDialog from "./ScheduleChangeDialog";
import PlanSkipDialog from "./PlanSkipDialog";

/**
 * P3.3 — client review page (design PDF page 2), "Review N of M".
 * NEW path /coach/review/:submissionId. Replaces, for reviewing, the old
 * Submission detail (/tracker-submissions/:id) + Member profile
 * (/member/member-profile/:id) round trip. Old pages stay and are linked.
 * Layout = PDF page 2: breadcrumb + "Review N of M", client header with the
 * reply pill, info strip (Submitted / Reply due / Next check-in / Check-in
 * day · Change / Review plan · Skip review), three columns (Latest check-in /
 * Programme / Coach notebook), Coach checklist, "Write feedback".
 * Phone (PDF page 3): Check-in / Programme / Notes tabs.
 * Write feedback → /coach/review/:id/feedback (PDF page 7). "Classic
 * editor" still opens the old feedback drawer.
 * CLIENTS (3 Nov): "Clients" / the client's name → /coach/clients(/:key);
 * "Submit check-in for client" = the old People-tab dialog.
 * ACTIONS-MENU (3 Nov): header = Chat icon + "⋮" (Login as member, Client
 * workspace, Submit check-in for client); strip = "⋮" (Review plan, Skip
 * review); check-in day = day name + pencil. Breadcrumb Today / name / Review.
 */

function StripItem({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Eyebrow sx={{ fontSize: 10.5 }}>{label}</Eyebrow>
      <Box sx={{ fontSize: 14, fontWeight: 600, color: CW.ink, mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

export default function ClientReviewPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [checklistState, setChecklistState] = useState("idle");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [dayOpen, setDayOpen] = useState(false);
  const [planTab, setPlanTab] = useState(null);
  const [week, setWeek] = useState(null);
  const [mobileTab, setMobileTab] = useState("checkin");
  const submitRef = useRef(null);

  const load = useCallback(async () => {
    setError("");
    const result = await getReviewContextApi(submissionId);
    if (result?.code === 200) {
      setCtx(result);
      setChecklist(result.reviewWork?.checklist || {});
    } else setError(result?.message || "Could not load this review");
  }, [submissionId]);

  useEffect(() => {
    setCtx(null);
    load();
  }, [load]);

  const submission = useMemo(() => (ctx ? normalizeSubmission(ctx.submission) : null), [ctx]);

  const goNext = () => {
    const nextId = ctx?.queue?.nextId;
    if (nextId) navigate(`/coach/review/${nextId}`);
    else navigate("/coach/today");
  };

  const saveChecklist = async (next) => {
    setChecklistState("saving");
    const result = await saveReviewChecklistApi(submissionId, next);
    if (result?.code === 200) setChecklistState("saved");
    else {
      setChecklistState("error");
      enqueueSnackbar(result?.message || "Checklist not saved", { variant: "error" });
    }
    return result?.code === 200;
  };

  const toggle = async (key, value) => {
    const next = { ...checklist, [key]: value };
    setChecklist(next);
    await saveChecklist(next);
  };

  const complete = async () => {
    setBusy(true);
    const result = await completeReviewWorkApi(submissionId, checklist);
    setBusy(false);
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Review work complete", { variant: "success" });
      load();
    } else enqueueSnackbar(result?.message || "Could not complete", { variant: "error" });
  };

  const noFeedback = async () => {
    setBusy(true);
    const result = await exceptFeedbackApi(submissionId, "No feedback needed");
    setBusy(false);
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Marked as no feedback needed", { variant: "success" });
      goNext();
    } else enqueueSnackbar(result?.message || "Could not save", { variant: "error" });
  };

  const loginAsMember = async () => {
    if (!submission?.email) {
      enqueueSnackbar("This client has no email on file", { variant: "warning" });
      return;
    }
    const result = await member_login_api({ email: submission.email, user_type: "client" });
    if (result?.code === 200) window.open(`${clientPortalUrl}login-by-admin/${result.token}`);
    else enqueueSnackbar(result?.message || "Could not log in as member", { variant: "error" });
  };

  const openCalendar = () => {
    const memberId = ctx?.person?.memberId;
    if (!memberId) return;
    const programmeId = (week?.tasks || []).find((t) => t.programmeId)?.programmeId;
    if (programmeId) navigate(`/member/${memberId}/member-MemberCalendar/${programmeId}`);
    else navigate(`/member/member-profile/${memberId}`);
  };

  if (error && !ctx) return <ErrorBox message={error} onRetry={load} />;
  if (!ctx || !submission) return <Loading />;

  const { person, tracker, reply, schedule, queue } = ctx;
  const feedbackDone = Boolean(ctx.feedbackSent || ctx.feedbackExcepted);
  const hasDraft = Boolean(ctx.feedbackDraft && (ctx.feedbackDraft.message || (ctx.feedbackDraft.media || []).length));
  // CLIENTS (3 Nov): the client's name opens the new client workspace
  // (/coach/clients/:key), which replaces the old Member profile for coaching.
  const clientKey = person?.memberId || person?.personKey || submission.email || "";
  const openProfile = clientKey ? () => navigate(`/coach/clients/${encodeURIComponent(clientKey)}`) : undefined;
  const writeFeedback = () => navigate(`/coach/review/${submission.id}/feedback`);
  const dayName = String(schedule?.current?.label || "").split("·")[0].trim();
  const statusPill = feedbackDone ? (
    <Pill tone="green" sx={{ fontSize: 13.5, px: 2, py: 0.75 }}>
      {ctx.feedbackSent ? "Feedback sent" : "No feedback needed"}
    </Pill>
  ) : (
    <Pill tone={reply?.overdue ? "red" : "teal"} sx={{ fontSize: 13.5, px: 2, py: 0.75 }}>
      {reply?.overdue ? `Reply ${String(reply.deadlineLabel || "overdue").replace(/^Overdue/i, "overdue")}` : reply?.deadlineLabel}
    </Pill>
  );
  const col = (key) => ({ display: { xs: mobileTab === key ? "block" : "none", lg: "block" }, minWidth: 0 });

  return (
    <div className="container-fluid">
      <Box sx={{ maxWidth: 1320, mx: "auto", pb: { xs: 10, lg: 4 } }}>
        {/* Breadcrumb + Review N of M */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Breadcrumbs
            items={[
              { label: "Today", onClick: () => navigate("/coach/today") },
              { label: person?.name || "Client", onClick: openProfile },
              { label: "Review" },
            ]}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mb: 1 }}>
            <Typography sx={{ fontSize: 14, color: CW.muted }}>
              {queue?.position ? `Review ${queue.position} of ${queue.total}` : "Not in your queue"}
            </Typography>
            <IconButton
              size="small"
              aria-label="Previous review"
              disabled={!queue?.prevId}
              onClick={() => navigate(`/coach/review/${queue.prevId}`)}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Next review"
              disabled={!queue?.nextId}
              onClick={() => navigate(`/coach/review/${queue.nextId}`)}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Client header */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start", justifyContent: "space-between", mt: 1, mb: 2.5 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", minWidth: 0 }}>
            <Initials name={person?.name} size={44} />
            <Box sx={{ minWidth: 0 }}>
              <PageTitle>
                {person?.name}
                <InfoHint id="review.header" />
              </PageTitle>
              <Typography sx={{ fontSize: { xs: 13.5, md: 15 }, color: CW.muted, mt: 0.5 }}>
                {[tracker?.name, submission.formName || tracker?.cadenceLabel, moment(reply?.submittedAt).format("D MMMM")]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", md: "flex-end" }, gap: 1 }}>
            {statusPill}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {person?.memberId ? (
                <IconAction title="Chat" testId="review-chat" onClick={() => navigate(`/chat/?chat_id=${person.memberId}`)}>
                  <ChatBubbleOutlineIcon fontSize="small" />
                </IconAction>
              ) : null}
              <ActionMenu
                testId="review-actions"
                label="Client actions"
                items={[
                  { label: "Login as member", icon: <LoginIcon fontSize="small" />, onClick: loginAsMember, disabled: !submission.email, testId: "menu-login-member" },
                  { label: "Client workspace", icon: <PersonOutlineIcon fontSize="small" />, onClick: openProfile, hidden: !openProfile, testId: "menu-client-workspace" },
                  {
                    label: "Submit check-in for client",
                    icon: <AssignmentOutlinedIcon fontSize="small" />,
                    onClick: () => submitRef.current && submitRef.current.open(),
                    disabled: !tracker?.id,
                    testId: "menu-submit-for-client",
                  },
                ]}
              />
              <SubmitForClient
                ref={submitRef}
                hideTrigger
                trackerId={tracker?.id}
                person={{ memberId: person?.memberId, personKey: person?.personKey, email: submission.email || person?.email, name: person?.name }}
                onSaved={() => load()}
              />
            </Box>
          </Box>
        </Box>

        {/* Info strip */}
        <Panel info="review.strip" sx={{ mb: 2.5, py: { xs: 1.5, md: 1.75 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr)) auto" },
              gap: { xs: 1.5, md: 3 },
              alignItems: "center",
            }}
          >
            <StripItem label="Submitted">{moment(reply?.submittedAt).format("D MMM, HH:mm")}</StripItem>
            <StripItem label="Reply due">
              <Box component="span" sx={{ color: !feedbackDone && reply?.overdue ? CW.red : CW.ink }}>
                {reply?.reviewDueAt ? moment(reply.reviewDueAt).format("D MMM, HH:mm") : "—"}
              </Box>
            </StripItem>
            <StripItem label="Next check-in">{schedule?.next?.opensLabel || "—"}</StripItem>
            <StripItem label="Check-in day">
              <Box component="span" sx={{ color: CW.primary }}>
                {dayName || tracker?.cadenceLabel || "—"}
                {schedule?.current?.source === "person" ? " (own day)" : ""}
              </Box>
              {schedule?.canChangeDay ? (
                <IconAction
                  title="Change check-in day"
                  testId="change-day"
                  onClick={() => setDayOpen(true)}
                  sx={{ ml: 0.75, p: 0.25, border: 0, bgcolor: "transparent", verticalAlign: "middle" }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconAction>
              ) : null}
            </StripItem>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gridColumn: { xs: "1 / -1", md: "auto" } }}>
              <ActionMenu
                testId="review-plan-actions"
                label="Review plan and skip"
                items={[
                  { label: "Review plan", icon: <EventNoteOutlinedIcon fontSize="small" />, onClick: () => setPlanTab("plan"), hidden: !schedule, testId: "menu-review-plan" },
                  { label: "Skip review", icon: <SkipNextOutlinedIcon fontSize="small" />, onClick: () => setPlanTab("skipReview"), testId: "menu-skip-review" },
                ]}
              />
            </Box>
          </Box>
          {(schedule?.upcoming || []).some((u) => u.skipped) ? (
            <Typography sx={{ fontSize: 12.5, color: CW.amber, mt: 1 }}>
              Skipped: {(schedule.upcoming || []).filter((u) => u.skipped).map((u) => u.opensLabel).join(", ")}
            </Typography>
          ) : null}
        </Panel>

        {/* Phone: one column at a time (PDF page 3) */}
        <UnderlineTabs
          value={mobileTab}
          onChange={setMobileTab}
          sx={{ display: { xs: "flex", lg: "none" }, mb: 2 }}
          options={[
            { value: "checkin", label: "Check-in" },
            { value: "programme", label: "Programme" },
            { value: "notes", label: "Notes" },
          ]}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1.15fr) minmax(0, 0.95fr)" },
            gap: 2.5,
            alignItems: "stretch",
            mb: 2.5,
          }}
        >
          {/* Latest check-in */}
          <Box sx={col("checkin")}>
            <LatestCheckInPanel
              submission={submission}
              history={ctx.history}
              onOpenFull={() => navigate(`/tracker-submissions/${submission.id}`)}
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            />
          </Box>

          {/* Programme */}
          <Box sx={col("programme")}>
            <ProgrammeWeekPanel
              memberId={person?.memberId}
              onWeekLoaded={setWeek}
              onOpenCalendar={person?.memberId ? openCalendar : undefined}
              clientFirstName={submission.firstName || String(person?.name || "").split(" ")[0]}
              sx={{ height: "100%" }}
            />
          </Box>

          {/* Coach notebook */}
          <Box sx={col("notes")}>
            <CoachNotebook
              memberId={person?.memberId}
              submissionId={submission.id}
              onOpenFeedback={writeFeedback}
              sx={{ height: "100%" }}
            />
          </Box>
        </Box>

        <ReviewChecklist
          checklist={checklist}
          onToggle={toggle}
          onSave={() => saveChecklist(checklist)}
          onComplete={complete}
          completedAt={ctx.reviewWork?.completedAt}
          busy={busy}
          feedbackDone={feedbackDone}
          saveState={checklistState}
        />

        {/* Bottom bar */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            mt: 2.5,
            position: { xs: "sticky", lg: "static" },
            bottom: { xs: 0, lg: "auto" },
            bgcolor: { xs: "#fff", lg: "transparent" },
            borderTop: { xs: `1px solid ${CW.line}`, lg: 0 },
            p: { xs: 1.5, lg: 0 },
            borderRadius: { xs: "12px 12px 0 0", lg: 0 },
            zIndex: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 13.5, color: CW.muted, display: { xs: "none", md: "block" } }}>
              {feedbackDone
                ? ctx.feedbackSent
                  ? "Feedback has been sent for this check-in."
                  : `No feedback needed (${ctx.feedbackExcepted?.reason || ""}).`
                : "Feedback to send stays in Today until sent or explicitly excepted."}
              <InfoHint id="review.bottom" />
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <TextLink onClick={() => setFeedbackOpen(true)} sx={{ fontSize: 12, fontWeight: 500, color: CW.muted }}>
                Classic editor
              </TextLink>
              {!feedbackDone ? (
                <TextLink onClick={noFeedback} disabled={busy} sx={{ fontSize: 12.5, display: { xs: "inline", md: "none" } }}>
                  No feedback needed
                </TextLink>
              ) : null}
              <TextLink onClick={goNext} sx={{ fontSize: 12.5, display: { xs: "inline", md: "none" } }}>
                {queue?.nextId ? "Next review →" : "Back to Today"}
              </TextLink>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", width: { xs: "100%", md: "auto" } }}>
            {!feedbackDone ? (
              <Box component="span" sx={{ display: { xs: "none", md: "inline-flex" } }}>
                <button type="button" className="small-contained-outlined-button" style={{ padding: "11px 18px" }} onClick={noFeedback} disabled={busy}>
                  No feedback needed
                </button>
              </Box>
            ) : null}
            <Box component="span" sx={{ display: { xs: "none", md: "inline-flex" } }}>
              <button type="button" className="small-contained-outlined-button" style={{ padding: "11px 18px" }} onClick={goNext}>
                {queue?.nextId ? "Next review →" : "Back to Today"}
              </button>
            </Box>
            <button
              type="button"
              className="small-contained-button"
              style={{ padding: "11px 18px", minWidth: 240, flex: "1 0 auto" }}
              onClick={writeFeedback}
              disabled={busy}
            >
              {hasDraft ? "Continue feedback draft" : ctx.feedbackSent ? "Send more feedback" : "Write feedback"}
            </button>
          </Box>
        </Box>
      </Box>

      <ClientFeedbackDrawer
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        submission={submission}
        metrics={[]}
        onSent={() => load()}
      />
      {schedule ? (
        <ScheduleChangeDialog
          open={dayOpen}
          onClose={() => setDayOpen(false)}
          trackerId={tracker?.id}
          personKey={person?.personKey}
          memberId={person?.memberId}
          personName={person?.name}
          trackerName={tracker?.name}
          current={schedule.current}
          rules={schedule.rules}
          onSaved={() => load()}
        />
      ) : null}
      <PlanSkipDialog
        open={Boolean(planTab)}
        initialTab={planTab || "plan"}
        onClose={() => setPlanTab(null)}
        submissionId={submission.id}
        trackerId={tracker?.id}
        personKey={person?.personKey}
        memberId={person?.memberId}
        personName={person?.name}
        reply={reply}
        schedule={schedule}
        onChangeDay={schedule?.canChangeDay ? () => { setPlanTab(null); setDayOpen(true); } : undefined}
        onSaved={(result) => (planTab === "skipReview" || result?.feedbackExcepted ? goNext() : load())}
      />
    </div>
  );
}
